import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@textile-erp/ui';
import { getModuleNavigation } from '../../config/modules.config';
import { useMenuContext } from '../../providers/MenuProvider';
import { useAuthContext } from '../../providers/AuthProvider';
import {
  Home,
  Users,
  Shield,
  Settings,
  BarChart3,
  ChevronRight,
  Search,
  Star,
  Clock,
  Minimize2,
  Maximize2,
  LayoutDashboard,
  Package,
  Palette,
  Moon,
  Sun,
  User,
  MoreVertical,
  LogOut,
  DollarSign,
  ShoppingCart,
  KeyRound
} from 'lucide-react';
import { ChangeOwnPasswordModal } from '../../components/ChangeOwnPasswordModal';

// Helper function to get active state styles using CSS variables
const getActiveStateStyle = (theme: any) => ({
  backgroundColor: 'var(--color-primary-100)',
  color: 'var(--color-primary-700)',
  borderColor: 'var(--color-primary-200)',
  ...(theme.mode === 'dark' && {
    backgroundColor: 'color-mix(in srgb, var(--color-primary-900) 30%, transparent)',
    color: 'var(--color-primary-200)',
    borderColor: 'var(--color-primary-700)'
  })
});


// Icon mapping for module navigation
const iconMap: Record<string, any> = {
  'LayoutDashboard': LayoutDashboard,
  'Users': Users,
  'Shield': Shield,
  'Home': Home,
  'BarChart3': BarChart3,
  'Settings': Settings,
  'Package': Package,
  'palette': Palette,
  'TrendingUp': BarChart3,
  'DollarSign': DollarSign,
  'ShoppingCart': ShoppingCart,
};

// Convert module navigation to internal format with icons
function convertModuleNavigation(moduleNav: any[]): NavigationItem[] {
  return moduleNav.map(item => ({
    name: item.name,
    href: item.path,
    icon: iconMap[item.icon || 'Home'] || Home,
    key: item.id,
    category: 'main',
    description: item.description,
    permissions: []
  }));
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  disabled?: boolean;
  children?: NavigationItem[];
  key?: string;
  category?: string;
  description?: string;
  isNew?: boolean;
  isPinned?: boolean;
  permissions?: string[];
}

interface SidebarProps {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    roles?: { name: string }[];
  };
}

export function Sidebar({ user }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { theme, toggleMode } = useTheme();
  const { hasModuleAccess, hasAccess, userMenus } = useMenuContext();
  const { logout } = useAuthContext();
  const isCompact = theme?.layoutDensity === 'compact';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['configuration']);
  const [searchQuery, setSearchQuery] = useState('');
  const [navigation, setNavigation] = useState<NavigationItem[]>([]);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [settingsDropdownPosition, setSettingsDropdownPosition] = useState({ top: 0, left: 0 });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);

  // Toggle settings dropdown
  const toggleSettingsDropdown = () => {
    if (!isSettingsDropdownOpen && settingsButtonRef.current) {
      const rect = settingsButtonRef.current.getBoundingClientRect();
      setSettingsDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
    setIsSettingsDropdownOpen(!isSettingsDropdownOpen);
  };

  // Handle theme toggle
  const handleThemeToggle = () => {
    toggleMode();
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/dashboard');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsDropdownRef.current && settingsButtonRef.current &&
          !settingsDropdownRef.current.contains(event.target as Node) &&
          !settingsButtonRef.current.contains(event.target as Node)) {
        setIsSettingsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get navigation from module configuration
  useEffect(() => {
    const moduleNavigation = getModuleNavigation();
    const convertedNav = convertModuleNavigation(moduleNavigation);
    setNavigation(convertedNav);
  }, []);

  // Utility navigation items (Pinned/Recent) - separate from main navigation
  const utilityNavigation = [
    {
      name: 'Pinned',
      href: '/pinned',
      icon: Star,
      key: 'pinned',
      category: 'utility',
      description: 'Your favorite items',
      permissions: []
    },
    {
      name: 'Recent',
      href: '/recent',
      icon: Clock,
      key: 'recent',
      category: 'utility',
      description: 'Recently visited items',
      permissions: []
    }
  ];

  // Filter navigation based on menu permissions from MenuProvider
  const filteredNavigation = useMemo(() => {
    return navigation.filter(item => {
      // Always show dashboard
      if (item.href === '/' || item.href === '/dashboard') {
        return true;
      }
      // Always show UI Examples (development/documentation tool)
      if (item.href === '/ui-examples') {
        return true;
      }
      // Check if user has access to this module
      return item.href ? hasModuleAccess(item.href) : true;
    });
  }, [navigation, hasModuleAccess]);
  
  // "Search menu…": every screen the user may open (the DB menu tree), matched on its name and
  // where it sits (e.g. "Purchase Management › Reports"). Every typed word must match the START of
  // a word ("empl" finds Employee, not Template).
  const searchResults = useMemo(() => {
    const words = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];
    const found: { label: string; trail: string; path: string }[] = [];
    const seen = new Set<string>();
    const walk = (nodes: typeof userMenus, trail: string[]) => {
      for (const node of nodes) {
        if (!node.isActive) continue;
        const label = node.displayName || node.name;
        if (node.path && !seen.has(node.path) && hasAccess(node.path)) {
          const hayWords = `${label} ${trail.join(' ')}`.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
          if (words.every((w) => hayWords.some((hw) => hw.startsWith(w)))) {
            seen.add(node.path);
            found.push({ label, trail: trail.join(' › '), path: node.path });
          }
        }
        if (node.children?.length) walk(node.children, [...trail, label]);
      }
    };
    walk(userMenus, []);
    const first = words[0];
    return found
      .sort((a, b) => Number(!a.label.toLowerCase().startsWith(first)) - Number(!b.label.toLowerCase().startsWith(first)) || a.label.localeCompare(b.label))
      .slice(0, 40);
  }, [searchQuery, userMenus, hasAccess]);

  const openSearchResult = (path: string) => {
    setSearchQuery('');
    navigate(path);
  };

  // Use dynamic sidebar mode from theme context
  const sidebarTheme = theme.sidebarMode === 'apparent' ? 'gradient' : 'default';

  // Auto-expand parent items when child is active
  useEffect(() => {
    if (navigation.length === 0) return; // Wait for navigation to be loaded
    
    const shouldExpand: string[] = [];
    
    const checkAndExpand = (items: NavigationItem[]) => {
      items.forEach(item => {
        if (item.children) {
          const hasActiveChild = item.children.some(child => pathname === child.href);
          if (hasActiveChild && item.key) {
            shouldExpand.push(item.key);
          }
          checkAndExpand(item.children);
        }
      });
    };
    
    checkAndExpand(navigation);
    
    if (shouldExpand.length > 0) {
      setExpandedItems(prev => {
        const newExpanded = [...new Set([...prev, ...shouldExpand])];
        return newExpanded;
      });
    }
  }, [pathname, navigation]); // Added navigation back but with guard


  const getSidebarClasses = () => {
    const baseClasses = `${isCollapsed ? 'w-16 sm:w-20' : 'w-72 sm:w-80'} transition-all duration-500 ease-in-out flex flex-col relative overflow-visible hover:shadow-lg z-40`;
    
    return `${baseClasses} border-r ${
      sidebarTheme === 'gradient' 
        ? 'border-primary-700/30 shadow-lg' 
        : 'bg-white dark:bg-gray-900 border-secondary-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-800'
    }`;
  };

  const getSidebarStyles = () => {
    if (sidebarTheme === 'gradient') {
      return {
        background: `linear-gradient(135deg, 
          var(--color-primary-900) 0%, 
          var(--color-primary-800) 30%, 
          var(--color-primary-700) 60%, 
          var(--color-primary-800) 90%, 
          var(--color-primary-900) 100%)`,
      };
    }
    return {};
  };

  return (
    <div className={`${getSidebarClasses()} ${theme.mode === 'dark' ? 'dark' : ''}`} style={getSidebarStyles()}>
      {/* Enhanced Sidebar Header */}
      <div 
        className={`${isCollapsed ? 'h-20 sm:h-24' : 'h-14 sm:h-16'} px-3 sm:px-4 border-b ${
          sidebarTheme === 'gradient' 
            ? 'border-primary-700/30' 
            : 'border-secondary-200 dark:border-gray-700'
        }`}
        style={sidebarTheme === 'gradient' ? {
          background: `linear-gradient(135deg, 
            rgba(0, 0, 0, 0.2) 0%, 
            rgba(0, 0, 0, 0.15) 50%, 
            rgba(0, 0, 0, 0.2) 100%)`,
          backdropFilter: 'blur(8px)',
        } : theme.mode === 'dark' ? {
          background: `linear-gradient(to right, color-mix(in srgb, var(--color-primary-800) 25%, var(--color-gray-900)), color-mix(in srgb, var(--color-primary-800) 25%, var(--color-gray-900)))`
        } : {
          background: `linear-gradient(to right, color-mix(in srgb, var(--color-primary-100) 25%, white), color-mix(in srgb, var(--color-primary-100) 25%, white))`
        }}
      >
        {isCollapsed ? (
          /* Collapsed Layout - Vertical Stack */
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            {/* Logo */}
            <div className="flex justify-center">
              <div 
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center"
                style={sidebarTheme === 'gradient' ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                } : theme.mode === 'dark' ? {
                  backgroundColor: `color-mix(in srgb, var(--color-primary-800) 50%, transparent)`
                } : {
                  backgroundColor: `var(--color-primary-100)`
                }}
              >
                <span className={`text-sm font-bold ${
                  sidebarTheme === 'gradient' ? 'text-white' : ''
                }`}
                style={sidebarTheme === 'default' ? {
                  color: `var(--color-primary-600)`
                } : {}}>T</span>
              </div>
            </div>
            
            {/* Settings & Expand Toggle */}
            <div className="flex items-center justify-center space-x-1">
              <button
                ref={!isCollapsed ? undefined : settingsButtonRef}
                onClick={toggleSettingsDropdown}
                className={`p-1 sm:p-1.5 rounded-lg transition-all duration-200 ${
                  sidebarTheme === 'gradient'
                    ? 'text-white hover:text-white hover:bg-white/30 bg-white/20 border border-white/30 shadow-xl backdrop-blur-sm'
                    : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700 hover:text-primary-600 border border-secondary-200 dark:border-gray-600'
                }`}
                title="Settings"
              >
                <MoreVertical className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>
              <button
                onClick={() => setIsCollapsed(false)}
                className={`p-1 sm:p-1.5 rounded-lg transition-all duration-200 ${
                  sidebarTheme === 'gradient'
                    ? 'text-white hover:text-white hover:bg-white/30 bg-white/20 border border-white/30 shadow-xl backdrop-blur-sm'
                    : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700 hover:text-primary-600 border border-secondary-200 dark:border-gray-600'
                }`}
                title="Expand sidebar"
              >
                <Maximize2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          /* Expanded Layout - Horizontal */
          <div className="flex items-center justify-between h-full w-full">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={sidebarTheme === 'gradient' ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                } : theme.mode === 'dark' ? {
                  backgroundColor: `color-mix(in srgb, var(--color-primary-800) 50%, transparent)`
                } : {
                  backgroundColor: `var(--color-primary-100)`
                }}
              >
                <span className={`text-lg font-bold ${
                  sidebarTheme === 'gradient' ? 'text-white' : ''
                }`}
                style={sidebarTheme === 'default' ? {
                  color: `var(--color-primary-600)`
                } : {}}>T</span>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${
                  sidebarTheme === 'gradient' ? 'text-white' : 'text-secondary-900 dark:text-gray-100'
                }`}>
                  AK Ahamed
                </h2>
                <p className={`text-xs mt-0.5 ${
                  sidebarTheme === 'gradient' ? 'text-white/70' : 'text-secondary-500 dark:text-gray-400'
                }`}>
                  Management System
                </p>
              </div>
            </div>
            
            {/* Right Side Controls */}
            <div className="flex items-center space-x-2">
              {/* Settings Dropdown */}
              <button
                ref={settingsButtonRef}
                onClick={toggleSettingsDropdown}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  sidebarTheme === 'gradient'
                    ? 'text-white hover:text-white hover:bg-white/30 bg-white/20 border border-white/30 shadow-xl backdrop-blur-sm'
                    : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700 hover:text-primary-600 border border-secondary-200 dark:border-gray-600'
                }`}
                title="Settings"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {/* Collapse Toggle */}
              <button
                onClick={() => setIsCollapsed(true)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  sidebarTheme === 'gradient'
                    ? 'text-white hover:text-white hover:bg-white/30 bg-white/20 border border-white/30 shadow-xl backdrop-blur-sm'
                    : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-100 dark:hover:bg-gray-700 hover:text-primary-600 border border-secondary-200 dark:border-gray-600'
                }`}
                title="Collapse sidebar"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <>
          {/* Search Bar */}
          <div className={`p-4 ${sidebarTheme === 'gradient' ? 'border-b border-primary-700/30' : 'border-b border-secondary-100'}`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${sidebarTheme === 'gradient' ? 'text-white/60' : 'text-secondary-400 dark:text-gray-500'}`} />
              <input
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                // Enter opens the first match, Esc clears (not the form Enter-to-next-field)
                data-enter="keep"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults[0]) { e.preventDefault(); openSearchResult(searchResults[0].path); }
                  if (e.key === 'Escape') setSearchQuery('');
                }}
                className={`w-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 transition-colors rounded-lg ${
                  sidebarTheme === 'gradient'
                    ? 'bg-white/10 border border-white/20 text-white placeholder-white/60 focus:ring-white/30 focus:border-white/40'
                    : 'bg-secondary-50 dark:bg-gray-800 border border-secondary-200 dark:border-gray-600 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500 dark:text-gray-200 dark:placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          {/* Quick Access Sections */}
          {!searchQuery && (
            <div 
            className={`px-4 py-3 border-b ${sidebarTheme === 'gradient' ? '' : 'border-secondary-100'}`}
            style={sidebarTheme === 'gradient' ? {
              borderBottomColor: `color-mix(in srgb, var(--color-primary-700) 30%, transparent)`
            } : {}}
          >
            </div>
          )}
        </>
      )}

      {/* Utility Navigation Section (Pinned/Recent) */}
      {!isCollapsed && !searchQuery && (
        <div className={`mx-3 mb-4 rounded-lg ${
          sidebarTheme === 'gradient' 
            ? 'bg-white/5' 
            : 'bg-gray-50/50 dark:bg-gray-800/30'
        }`}>
          <div className="px-3 py-3">
            <div className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
              sidebarTheme === 'gradient' 
                ? 'text-white/70' 
                : 'text-secondary-600 dark:text-gray-300'
            }`}>
              Quick Access
            </div>
            <div className="space-y-1">
              {utilityNavigation.map((item) => (
                <NavigationItem 
                  key={item.name} 
                  item={item} 
                  pathname={pathname}
                  sidebarTheme={sidebarTheme}
                  isCollapsed={false}
                  isCompact={isCompact}
                  expandedItems={expandedItems}
                  setExpandedItems={setExpandedItems}
                  searchQuery=""
                  theme={theme}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className={`flex-1 px-3 ${isCompact ? 'py-2 space-y-1' : 'py-4 space-y-2'} overflow-y-auto scrollbar-styled stagger-animation ${sidebarTheme === 'gradient' ? 'sidebar-scroll-gradient' : 'sidebar-scroll'}`}>
        {!searchQuery && (
          <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
            sidebarTheme === 'gradient'
              ? 'text-white/60'
              : 'text-secondary-500 dark:text-gray-400'
          }`}>
            Modules
          </div>
        )}
        {searchQuery && (
          <div className="space-y-1">
            {searchResults.length === 0 ? (
              <div className={`px-3 py-2 text-sm ${sidebarTheme === 'gradient' ? 'text-white/70' : 'text-secondary-500 dark:text-gray-400'}`}>
                No screen found
              </div>
            ) : (
              searchResults.map((r) => (
                <Link
                  key={r.path}
                  to={r.path}
                  onClick={() => setSearchQuery('')}
                  className={`block px-3 py-2 rounded-lg transition-colors ${
                    sidebarTheme === 'gradient'
                      ? 'text-white hover:bg-white/10'
                      : 'text-secondary-700 hover:bg-secondary-100 dark:text-gray-200 dark:hover:bg-gray-700'
                  } ${pathname === r.path ? (sidebarTheme === 'gradient' ? 'bg-white/15' : 'bg-primary-50 dark:bg-gray-700') : ''}`}
                >
                  <div className="text-sm font-medium truncate">{r.label}</div>
                  {r.trail && (
                    <div className={`text-xs truncate ${sidebarTheme === 'gradient' ? 'text-white/60' : 'text-secondary-500 dark:text-gray-400'}`}>
                      {r.trail}
                    </div>
                  )}
                </Link>
              ))
            )}
          </div>
        )}
        {!searchQuery && filteredNavigation.map((item) => {
          return <NavigationItem
            key={item.name}
            item={item}
            pathname={pathname}
            sidebarTheme={sidebarTheme}
            isCollapsed={isCollapsed}
            isCompact={isCompact}
            expandedItems={expandedItems}
            setExpandedItems={setExpandedItems}
            searchQuery={searchQuery}
            theme={theme}
          />;
        })}
      </nav>

      {/* Settings Dropdown Portal */}
      {isSettingsDropdownOpen && typeof window !== 'undefined' && createPortal(
        <div
          ref={settingsDropdownRef}
          className="fixed w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl z-[9999] overflow-hidden animate-fadeInScale"
          style={{
            top: `${settingsDropdownPosition.top}px`,
            left: `${settingsDropdownPosition.left}px`,
          }}
        >
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-gray-800 dark:to-gray-750">
            <div className="flex items-center space-x-3">
              <div
                className="h-10 w-10 rounded-xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(to bottom right, var(--color-primary-400), var(--color-primary-500))`,
                }}
              >
                <span className="text-sm font-semibold text-white">
                  {(user.firstName?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={handleThemeToggle}
              className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center">
                {theme.mode === 'light' ? (
                  <Moon className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                ) : (
                  <Sun className="w-4 h-4 mr-3 text-yellow-500" />
                )}
                <span>{theme.mode === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
              </div>
              <div className={`w-10 h-5 rounded-full transition-colors ${theme.mode === 'dark' ? 'bg-primary-500' : 'bg-gray-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform mt-0.5 ${theme.mode === 'dark' ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </button>

            {/* Theme Settings */}
            <button
              onClick={() => {
                setIsSettingsDropdownOpen(false);
                // Trigger theme settings panel - dispatch custom event
                window.dispatchEvent(new CustomEvent('openThemeSettings'));
              }}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Palette className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
              <span>Theme Settings</span>
            </button>

            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

            {/* Profile Settings */}
            <Link
              to="/profile"
              onClick={() => setIsSettingsDropdownOpen(false)}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <User className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
              <span>Profile Settings</span>
            </Link>

            {/* Account Settings */}
            <Link
              to="/settings"
              onClick={() => setIsSettingsDropdownOpen(false)}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Settings className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
              <span>Account Settings</span>
            </Link>

            {/* Change Password */}
            <button
              onClick={() => {
                setIsSettingsDropdownOpen(false);
                setShowChangePasswordModal(true);
              }}
              className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <KeyRound className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
              <span>Change Password</span>
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      <ChangeOwnPasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        email={user.email}
      />
    </div>
  );
}

interface NavigationItemProps {
  item: NavigationItem;
  pathname: string;
  sidebarTheme: 'default' | 'gradient';
  isCollapsed: boolean;
  isCompact: boolean;
  expandedItems: string[];
  setExpandedItems: (items: string[]) => void;
  searchQuery: string;
  theme: any;
}

function NavigationItem({ 
  item, 
  pathname, 
  sidebarTheme, 
  isCollapsed,
  isCompact,
  expandedItems, 
  setExpandedItems,
  searchQuery,
  theme,
}: NavigationItemProps) {
  const Icon = item.icon;
  // Check if the current path is under this module's path
  // Special handling for root path (Dashboard)
  const isModuleActive = item.href && (
    item.href === '/' 
      ? pathname === '/' 
      : pathname.startsWith(item.href)
  );
  const isDisabled = item.disabled;
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.key!) || searchQuery.length > 0;
  const isChildActive = hasChildren && item.children?.some(child => pathname === child.href);

  const toggleExpand = () => {
    if (hasChildren && item.key) {
      setExpandedItems(
        isExpanded && !searchQuery
          ? expandedItems.filter(key => key !== item.key)
          : [...expandedItems, item.key]
      );
    }
  };

  // Collapsed sidebar - show icons for all items including submenus
  if (isCollapsed) {
    if (hasChildren) {
      return (
        <div className="space-y-1">
          {/* Parent category icon with tooltip */}
          <div
            className={`flex items-center justify-center p-3 rounded-xl transition-all duration-200 cursor-pointer ${
              sidebarTheme === 'gradient'
                ? 'text-white/70 hover:text-white hover:bg-white/10'
                : 'text-secondary-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-secondary-50 dark:hover:bg-gray-800'
            }`}
            title={item.name}
          >
            <Icon className={`w-5 h-5 ${
              sidebarTheme === 'gradient'
                ? 'text-white/60 hover:text-white'
                : 'text-secondary-400 hover:text-primary-600'
            }`} />
          </div>
          
          {/* Submenu icons */}
          <div className="pl-3 space-y-1">
            {item.children!.map((subItem) => {
              if (!subItem.href) return null;
              
              const SubIcon = subItem.icon;
              const isSubActive = pathname === subItem.href;
              
              return (
                <Link
                  key={subItem.href}
                  to={subItem.href}
                  className={`flex items-center justify-center p-2 rounded-lg transition-all duration-200 relative border ${
                    isSubActive
                      ? sidebarTheme === 'gradient'
                        ? 'text-white bg-white/20 border-white/30'
                        : ''
                      : sidebarTheme === 'gradient'
                        ? 'text-white/70 hover:text-white hover:bg-white/10 border-transparent'
                        : 'text-secondary-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent'
                  }`}
                  style={isSubActive && sidebarTheme !== 'gradient' ? getActiveStateStyle(theme) : {}}
                  title={subItem.name}
                >
                  <SubIcon className={`w-4 h-4 ${
                    isSubActive 
                      ? sidebarTheme === 'gradient' ? 'text-white' : 'text-primary-600'
                      : sidebarTheme === 'gradient'
                        ? 'text-white/60 hover:text-white'
                        : 'text-secondary-400 hover:text-primary-600'
                  }`} />
                  {subItem.isNew && (
                    <div className="absolute -top-1 -left-1 w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Regular menu items with tooltips
    if (item.href) {
      return (
        <Link
          to={item.href}
          className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 relative border hover:scale-105 hover-lift micro-bounce ${
            isModuleActive
              ? sidebarTheme === 'gradient'
                ? 'text-white bg-white/20 border-white/30 shadow-lg animate-pulseScale'
                : 'shadow-md'
              : sidebarTheme === 'gradient'
                ? 'text-white/70 hover:text-white hover:bg-white/10 border-transparent hover:shadow-lg'
                : 'text-secondary-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent hover:shadow-md'
          }`}
          style={isModuleActive && sidebarTheme !== 'gradient' ? getActiveStateStyle(theme) : {}}
          title={item.name}
        >
          <Icon className={`w-5 h-5 ${
            isModuleActive 
              ? sidebarTheme === 'gradient' ? 'text-white' : 'text-primary-600'
              : sidebarTheme === 'gradient'
                ? 'text-white/60 hover:text-white'
                : 'text-secondary-400 hover:text-primary-600'
          }`} />
        </Link>
      );
    }
    
    if (isDisabled) {
      return (
        <div
          className={`flex items-center justify-center p-3 rounded-xl cursor-not-allowed opacity-50 ${
            sidebarTheme === 'gradient' ? 'text-white/40' : 'text-secondary-400'
          }`}
          title={item.name}
        >
          <Icon className="w-5 h-5" />
        </div>
      );
    }
    
    return null;
  }
  
  // Regular sidebar - full menu
  if (hasChildren) {
    return (
      <div>
        {/* Category Header */}
        <div className={`flex items-center justify-between ${isCompact ? 'mb-1' : 'mb-2'}`}>
          <button
            onClick={toggleExpand}
            className={`w-full group flex items-center justify-between ${isCompact ? 'px-2 py-2' : 'px-3 py-3'} text-sm font-semibold rounded-xl transition-all duration-200 ${
              isChildActive
                ? sidebarTheme === 'gradient'
                  ? 'text-white'
                  : 'bg-gradient-to-r from-primary-50 to-primary-50 text-primary-700'
                : sidebarTheme === 'gradient'
                  ? 'text-white/90 hover:text-white hover:bg-gradient-to-r hover:from-white/10 hover:to-white/5'
                  : 'text-secondary-700 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gradient-to-r hover:from-primary-25 hover:to-primary-25'
            }`}
            style={{
              ...(isChildActive && sidebarTheme === 'gradient' ? {
                background: `linear-gradient(to right, color-mix(in srgb, var(--color-primary-200) 20%, transparent), color-mix(in srgb, var(--color-primary-100) 10%, transparent))`
              } : {})
            }}
          >
            <div className="flex items-center">
              <Icon className={`w-5 h-5 ${isCompact ? 'mr-2' : 'mr-3'} transition-colors ${
                isChildActive 
                  ? sidebarTheme === 'gradient' ? 'text-white' : 'text-primary-600'
                  : sidebarTheme === 'gradient'
                    ? 'text-white/70 group-hover:text-white'
                    : 'text-secondary-500 group-hover:text-primary-600'
              }`} />
              <div className="text-left">
                <div>{item.name}</div>
                {!isCompact && item.description && (
                  <div className={`text-xs font-normal mt-0.5 ${
                    sidebarTheme === 'gradient' ? 'text-white/60' : 'text-secondary-500'
                  }`}>
                    {item.description}
                  </div>
                )}
              </div>
            </div>
            {!searchQuery && (
              <ChevronRight className={`w-4 h-4 transition-transform ${
                isExpanded ? 'rotate-90' : 'rotate-0'
              } ${sidebarTheme === 'gradient' ? 'text-white/70' : ''}`} />
            )}
          </button>
        </div>
        
        {/* Submenu items */}
        {isExpanded && (
          <div className={`${isCompact ? 'ml-6 space-y-1 pb-2' : 'ml-8 space-y-1 pb-4'} border-l-2 ${
            sidebarTheme === 'gradient' ? 'border-white/20' : 'border-secondary-100'
          }`}>
            {item.children!.map((subItem) => {
              if (!subItem.href) return null;
              
              const SubIcon = subItem.icon;
              const isSubActive = pathname === subItem.href;
              
              return (
                <Link
                  key={subItem.href}
                  to={subItem.href}
                  className={`group flex items-center ${isCompact ? 'px-2 py-1.5 ml-2' : 'px-3 py-2.5 ml-3'} text-sm font-medium rounded-lg transition-all duration-200 relative border ${
                    isSubActive
                      ? sidebarTheme === 'gradient'
                        ? 'text-white shadow-sm bg-white/20 border-white/30'
                        : 'shadow-sm'
                      : sidebarTheme === 'gradient'
                        ? 'text-white/70 hover:text-white hover:bg-white/10 border-transparent'
                        : 'text-secondary-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent'
                  }`}
                  style={isSubActive && sidebarTheme !== 'gradient' ? getActiveStateStyle(theme) : {}}
                >
                  <SubIcon className={`w-4 h-4 ${isCompact ? 'mr-2' : 'mr-3'} transition-colors ${
                    isSubActive 
                      ? sidebarTheme === 'gradient' ? 'text-white' : 'text-primary-600'
                      : sidebarTheme === 'gradient'
                        ? 'text-white/60 group-hover:text-white'
                        : 'text-secondary-400 group-hover:text-primary-600'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span>{subItem.name}</span>
                      {subItem.isNew && (
                        <span className="ml-2 px-1.5 py-0.5 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs font-bold rounded-full">
                          NEW
                        </span>
                      )}
                      {subItem.isPinned && (
                        <Star className="w-3 h-3 ml-2 text-amber-500 fill-current" />
                      )}
                    </div>
                    {!isCompact && subItem.description && (
                      <div className={`text-xs mt-0.5 ${
                        sidebarTheme === 'gradient' ? 'text-white/50' : 'text-secondary-500'
                      }`}>
                        {subItem.description}
                      </div>
                    )}
                  </div>
                  
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  
  // Handle regular menu items
  if (!item.href) return null;
  
  if (isDisabled) {
    return (
      <div
        className={`flex items-center px-3 py-3 text-sm font-medium rounded-xl cursor-not-allowed opacity-50 ${
          sidebarTheme === 'gradient' ? 'text-white/40' : 'text-secondary-400'
        }`}
      >
        <Icon className="w-5 h-5 mr-3" />
        <span className="flex-1">{item.name}</span>
      </div>
    );
  }
  
  return (
    <Link
      to={item.href}
      className={`group flex items-center ${isCompact ? 'px-2 py-2' : 'px-3 py-3'} text-sm font-medium rounded-xl transition-all duration-200 border ${
        isModuleActive
          ? sidebarTheme === 'gradient'
            ? 'text-white shadow-lg bg-white/20 border-white/30'
            : 'shadow-lg'
          : sidebarTheme === 'gradient'
            ? 'text-white/70 hover:text-white hover:bg-white/10 border-transparent'
            : 'text-secondary-600 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent'
      }`}
      style={isModuleActive && sidebarTheme !== 'gradient' ? getActiveStateStyle(theme) : {}}
    >
      <Icon className={`w-5 h-5 ${isCompact ? 'mr-2' : 'mr-3'} transition-colors ${
        isModuleActive 
          ? sidebarTheme === 'gradient' ? 'text-white' : 'text-primary-600'
          : sidebarTheme === 'gradient'
            ? 'text-white/60 group-hover:text-white'
            : 'text-secondary-400 group-hover:text-primary-600'
      }`} />
      <div className="flex-1">
        <div>{item.name}</div>
        {!isCompact && item.description && (
          <div className={`text-xs mt-0.5 ${
            sidebarTheme === 'gradient' ? 'text-white/60' : 'text-secondary-500'
          }`}>
            {item.description}
          </div>
        )}
      </div>
    </Link>
  );
}